import { useState } from 'react';
import { Button, Grid, MenuItem, Popover } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { Form } from 'react-final-form';
import { map, omit } from 'lodash';
import { LoadingButton } from '@mui/lab';

import SelectField from 'common/forms/SelectField';
import DateField from 'common/forms/DateField';
import TextField from 'common/forms/TextField';
import { filterPatientsList } from 'store/thunks/patientsListThunks';
import gender from 'common/constants/gender';
import { validateMinLength } from 'common/forms/formValidations';
import { casingFormat } from 'common/utils';

const PatientFilterButton = () => {
  const dispatch = useDispatch();
  const [anchorEl, setAnchorEl] = useState(null);
  const filter = useSelector(({ patientsList }) => ({
    ...omit(patientsList.filter, 'comboSearch'),
    createdAfter: patientsList.filter.createdAfter && new Date(patientsList.filter.createdAfter),
    dob: patientsList.filter.dob && new Date(patientsList.filter.dob),
  }));

  const partners = useSelector(({ auth, partner }) => partner[auth.currentTenantId]);
  const loading = useSelector(({ patientsList }) => patientsList.loading);

  const handleOpen = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const popoverId = anchorEl ? 'PatientsList-PatientFilterButton-Popover' : undefined;

  const handleApplyFilter = (formData) => {
    setAnchorEl(null);

    dispatch(
      filterPatientsList({
        filter: {
          ...formData,
          createdAfter: formData.createdAfter && formData.createdAfter.toISOString(),
          dob: formData.dob && formData.dob.toISOString(),
        },
      })
    );
  };

  return (
    <>
      <Button
        variant='outlined'
        color='secondary'
        onClick={handleOpen}
        aria-describedby={popoverId}
      >
        Filters
      </Button>

      <Popover
        id={popoverId}
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Grid container sx={{ p: 1.5, width: 325 }} spacing={2} direction='column'>
          <Grid item>
            <Form
              initialValues={filter}
              onSubmit={handleApplyFilter}
              render={({ handleSubmit, invalid }) => (
                <form onSubmit={handleSubmit} noValidate>
                  <Grid item sx={{ py: 0.5 }}>
                    <TextField
                      label='Patient Name'
                      name='name'
                      id='PatientsList-PatientFilterButton-name-input'
                      validations={[validateMinLength('Minimum of 3 characters', 3)]}
                    />
                  </Grid>

                  <Grid item sx={{ py: 0.5 }}>
                    <SelectField
                      label='Status'
                      name='status'
                      id='PatientsList-PatientFilterButton-status-dropdown'
                    >
                      <MenuItem value=''>All</MenuItem>
                      <MenuItem value='OPEN'>Open</MenuItem>
                      <MenuItem value='QUALIFIED'>Qualified</MenuItem>
                      <MenuItem value='UNQUALIFIED'>Unqualified</MenuItem>
                      <MenuItem value='ONBOARDED'>Onboarded</MenuItem>
                      <MenuItem value='DEACTIVATED'>Deactivated</MenuItem>
                    </SelectField>
                  </Grid>

                  <Grid item sx={{ py: 0.5 }}>
                    <SelectField
                      label='Gender'
                      name='gender'
                      id='PatientsList-PatientFilterButton-gender-dropdown'
                    >
                      <MenuItem value=''>All</MenuItem>
                      {map(gender, (name, value) => (
                        <MenuItem value={value} key={value}>
                          {name}
                        </MenuItem>
                      ))}
                    </SelectField>
                  </Grid>

                  <Grid item sx={{ py: 0.5 }}>
                    <DateField
                      label='Created After'
                      name='createdAfter'
                      id='PatientsList-PatientFilterButton-createdAfter-dateField'
                      maxDate={new Date()}
                    />
                  </Grid>

                  <Grid item sx={{ py: 0.5 }}>
                    <DateField
                      label='Date of Birth'
                      name='dob'
                      id='PatientsList-PatientFilterButton-dateOfBirth-dateField'
                      maxDate={new Date()}
                    />
                  </Grid>

                  <Grid item sx={{ py: 0.5 }}>
                    <SelectField
                      label='Original Partner'
                      name='originalPartner'
                      id='PatientsList-PatientFilterButton-originalPartner-dropdown'
                      lexicographicSort={false}
                    >
                      <MenuItem value=''>All</MenuItem>
                      {[...partners]
                        .sort((partnerA, partnerB) =>
                          partnerA.partnerName.localeCompare(partnerB.partnerName)
                        )
                        .map(({ partnerName, partnerUuid }) => (
                          <MenuItem value={partnerUuid} key={partnerUuid}>
                            {casingFormat(partnerName)}
                          </MenuItem>
                        ))}
                    </SelectField>
                  </Grid>

                  <Grid container justifyContent='flex-end'>
                    <LoadingButton
                      loading={loading}
                      type='submit'
                      variant='contained'
                      disabled={invalid}
                    >
                      Apply
                    </LoadingButton>
                  </Grid>
                </form>
              )}
            />
          </Grid>
        </Grid>
      </Popover>
    </>
  );
};

export default PatientFilterButton;
